import { QueryClient, UseMutationOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { User } from '../models/User';
import OCGatewayClient from '../client/OCGatewayClient';
import { invalidateGatewayUserScopedQueries } from './gatewayQueryUtils';
import { queryClient as defaultQueryClient } from './queryClient';
import OCAuth from '../client/OCAuth';

export const currentUserQueryKey = ['currentUser'] as const;

const installedCurrentUserListeners = new WeakSet<QueryClient>();

const getUserIdentityKey = (user: User | null | undefined) => {
  return user ? `${user.id}:${user.userLevel ?? ''}` : 'anonymous';
};

const isMissingAuthTransactionError = (error: unknown) => {
  return typeof error === 'object'
    && error !== null
    && 'error' in error
    && (error as { error?: unknown }).error === 'missing_transaction';
};

const dispatchAuthError = (error: unknown) => {
  document.dispatchEvent(new CustomEvent('oc:auth:error', {
    detail: error
  }));
};

export function setCurrentUser(
  user: User | null | undefined,
  queryClient: QueryClient = defaultQueryClient,
  options?: { invalidateUserScoped?: boolean }
) {
  const previousUser = queryClient.getQueryData<User | null>(currentUserQueryKey);
  const previousUserIdentity = getUserIdentityKey(previousUser);
  const nextUser = user ?? null;
  const nextUserIdentity = getUserIdentityKey(nextUser);

  queryClient.setQueryData(currentUserQueryKey, nextUser);

  if (!options?.invalidateUserScoped && previousUserIdentity === nextUserIdentity) {
    return;
  }

  invalidateGatewayUserScopedQueries(queryClient);
}

export function installCurrentUserQueryListener(queryClient: QueryClient = defaultQueryClient) {
  if (installedCurrentUserListeners.has(queryClient)) {
    return;
  }

  OCGatewayClient.socket.on("userUpdate", (user: User | null) => {
    console.info("(currentUserQuery) userUpdate", user);
    setCurrentUser(user, queryClient, { invalidateUserScoped: true });
  });

  installedCurrentUserListeners.add(queryClient);
}

export async function handleAuthRedirectCallback(
  url?: string,
  queryClient: QueryClient = defaultQueryClient
): Promise<User | null> {
  try {
    const token = await OCAuth.completeRedirectCallback(url);
    const user = await OCGatewayClient.request('loginWithAuth0', { token }) as User;

    setCurrentUser(user, queryClient);

    return user;
  } catch (error) {
    if (isMissingAuthTransactionError(error)) {
      console.warn('(currentUserQuery) Ignoring missing Auth0 transaction', error);
      return null;
    }

    dispatchAuthError(error);
    console.error(error);
    throw error;
  }
}

export async function logoutCurrentUser(queryClient: QueryClient = defaultQueryClient): Promise<void> {
  await OCGatewayClient.request('logout', {});

  setCurrentUser(null, queryClient, { invalidateUserScoped: true });
}

export async function deleteCurrentUserAccount(queryClient: QueryClient = defaultQueryClient): Promise<void> {
  await OCGatewayClient.request('deleteAccount', {});

  setCurrentUser(null, queryClient, { invalidateUserScoped: true });
}

export function useCurrentUserQuery() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: currentUserQueryKey,
    queryFn: async (): Promise<User | null> => {
      return queryClient.getQueryData<User | null>(currentUserQueryKey) ?? null;
    },
    staleTime: Infinity,
    gcTime: 1000 * 60 * 60 * 24,
    networkMode: 'always',
    retry: false,
  });

  return query;
}

export function useHandleAuthRedirectCallbackMutation(
  options?: UseMutationOptions<User | null, unknown, string | undefined>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (url?: string) => handleAuthRedirectCallback(url, queryClient),
    ...options
  });
}

export function useLogoutMutation(options?: UseMutationOptions<void, unknown, void>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => logoutCurrentUser(queryClient),
    ...options
  });
}

export function useDeleteAccountMutation(options?: UseMutationOptions<void, unknown, void>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => deleteCurrentUserAccount(queryClient),
    ...options
  });
}
